import { useMemo, useState } from 'react'
import type { ContentCategory, ContentItem, Dynasty } from '../data/contentLibrary'
import { CATEGORY_LABELS, DYNASTY_LABELS, filterItems } from '../data/contentLibrary'

interface ArticleListPanelProps {
  items: ContentItem[]
  userItems: ContentItem[]
  activeContentId: string | null
  onClose: () => void
  onPick: (item: ContentItem) => void
}

const ALL = '' as const

export default function ArticleListPanel({
  items,
  userItems,
  activeContentId,
  onClose,
  onPick,
}: ArticleListPanelProps) {
  const allItems = useMemo(() => [...items, ...userItems], [items, userItems])
  const [category, setCategory] = useState<ContentCategory | ''>(ALL)
  const [dynasty, setDynasty] = useState<Dynasty | ''>(ALL)
  const [author, setAuthor] = useState<string>(ALL)
  const [query, setQuery] = useState('')

  const dynasties = useMemo(() => {
    const set = new Set<Dynasty>()
    for (const item of allItems) {
      if (!category || item.category === category) set.add(item.dynasty)
    }
    return (Object.keys(DYNASTY_LABELS) as Dynasty[]).filter((key) => set.has(key))
  }, [allItems, category])

  const authors = useMemo(() => {
    const set = new Set<string>()
    for (const item of allItems) {
      if (category && item.category !== category) continue
      if (dynasty && item.dynasty !== dynasty) continue
      set.add(item.source)
    }
    return [...set].sort((a, b) => a.localeCompare(b, 'zh-Hant'))
  }, [allItems, category, dynasty])

  const filtered = useMemo(() => {
    const base = filterItems(allItems, category, dynasty, author)
    const keyword = query.trim()
    if (!keyword) return base
    return base.filter((item) => {
      return [item.title, item.source, item.description, item.content].some((value) => value.includes(keyword))
    })
  }, [allItems, category, dynasty, author, query])

  const selectedText = [
    category ? CATEGORY_LABELS[category] : '',
    dynasty ? DYNASTY_LABELS[dynasty] : '',
    author,
    query.trim() ? `搜尋：${query.trim()}` : '',
  ].filter(Boolean).join(' · ')

  function resetFilters() {
    setCategory(ALL)
    setDynasty(ALL)
    setAuthor(ALL)
    setQuery('')
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-950/40 p-3 sm:p-6 print:hidden" role="dialog" aria-modal="true">
      <div className="flex h-full max-h-[85vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 px-5 py-3.5">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">文章列表</h2>
            <p className="mt-0.5 text-xs text-gray-500">
              {filtered.length} / {allItems.length} 篇{selectedText ? ` · ${selectedText}` : ''}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-gray-300 bg-white px-4 py-1.5 text-sm font-medium text-gray-700 hover:border-gray-400 hover:bg-gray-50"
          >
            關閉
          </button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-[1fr_1fr_1fr_1.5fr_auto] gap-2.5 border-b border-gray-200 bg-gray-50 px-4 py-3">
          <select
            value={category}
            onChange={(event) => {
              setCategory(event.target.value as ContentCategory | '')
              setDynasty(ALL)
              setAuthor(ALL)
            }}
            className="min-w-0 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm outline-none focus:border-blue-500"
          >
            <option value="">全部類別</option>
            {(Object.entries(CATEGORY_LABELS) as [ContentCategory, string][]).map(([key, label]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>

          <select
            value={dynasty}
            onChange={(event) => {
              setDynasty(event.target.value as Dynasty | '')
              setAuthor(ALL)
            }}
            className="min-w-0 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm outline-none focus:border-blue-500"
          >
            <option value="">全部朝代</option>
            {dynasties.map((key) => (
              <option key={key} value={key}>{DYNASTY_LABELS[key]}</option>
            ))}
          </select>

          <select
            value={author}
            onChange={(event) => setAuthor(event.target.value)}
            className="min-w-0 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm outline-none focus:border-blue-500"
          >
            <option value="">全部作者</option>
            {authors.map((name) => (
              <option key={name} value={name}>{name}</option>
            ))}
          </select>

          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="搜尋標題、作者、內容"
            className="min-w-0 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm outline-none focus:border-blue-500"
          />

          <button
            type="button"
            onClick={resetFilters}
            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100"
          >
            清除
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-auto">
          {filtered.length > 0 ? (
            <div className="divide-y divide-gray-100">
              {filtered.map((item) => {
                const active = item.id === activeContentId
                const isUser = userItems.some((userItem) => userItem.id === item.id)
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => onPick(item)}
                    className={`block w-full px-4 py-3 text-left hover:bg-blue-50 ${active ? 'bg-blue-50' : 'bg-white'}`}
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-gray-900">{item.title}</span>
                      <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-500">{item.source}</span>
                      <span className="rounded bg-gray-50 px-1.5 py-0.5 text-xs text-gray-400">{DYNASTY_LABELS[item.dynasty]}</span>
                      <span className="rounded bg-gray-50 px-1.5 py-0.5 text-xs text-gray-400">{CATEGORY_LABELS[item.category]}</span>
                      {isUser && <span className="rounded bg-green-50 px-1.5 py-0.5 text-xs text-green-600">自訂</span>}
                    </div>
                    <p className="mt-1 line-clamp-2 text-sm leading-6 text-gray-500">{item.description || item.content}</p>
                  </button>
                )
              })}
            </div>
          ) : (
            <div className="flex h-full items-center justify-center p-8 text-sm text-gray-500">
              沒有符合條件的文章
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
